/**
 * DecidableChange — wraps a `ChangeMarker` with the Phase 3.6 review UI:
 *
 *   1. Hover-revealed Accept / Reject overlay (250 ms delay so the page is
 *      not visually noisy when the reviewer is just scrolling).
 *   2. Final-state chip + 2px outline on the *winning* side once a decision
 *      is recorded ("已采纳" / "保留" — the side that becomes the final doc).
 *   3. The losing side is left to the owning view: it must pass
 *      `decisionDisplay='loser'` to the inner `ChangeMarker` so the marker
 *      paints itself gray with strikethrough.
 *
 * Why a wrapper rather than baking this into `ChangeMarker`:
 *   - `ChangeMarker` is reused in two views (side-by-side on both sides for
 *     modifications and moves; unified). Each instance must be wrapped
 *     individually so the overlay anchors to the right block (e.g. a
 *     modification gets two overlays — one per side — each capable of
 *     dispatching the same `decide` call).
 *   - Keeps the visual-language layer stable; only the *interactive* layer
 *     evolves with M3.
 *
 * Final-state visual encoding (M3 B5 — "user can't tell what they accepted"):
 *
 *     pending         → no extra ring, type-coded marker (default)
 *     winner side     → 2px solid green outline + chip "已采纳"/"保留";
 *                       the marker repaints itself green via decisionDisplay
 *     loser side      → no outline (the marker is already gray + strikethru
 *                       via decisionDisplay='loser') + chip "已弃用"/"已拒绝"
 *
 * The ring uses `outline` rather than `border` so it does *not* perturb the
 * existing `ChangeMarker` left-bar / padding maths.
 */
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { veridocApi, type ChangeType, type DecisionAction, type DecisionHistoryItem, type DecisionStatus } from '../../lib/veridoc-api';
import type { DecisionMeta } from './useDecisions';
import type { NavigationAdapter } from './navigationAdapter';
import type { DecisionDisplay } from './ChangeMarker';
import { colors } from '../../design-tokens/global';

/** Which side of a comparison this block represents.
 *   'A' = left / version A — wins on `rejected`, loses on `accepted`
 *   'B' = right / version B — wins on `accepted`, loses on `rejected`
 *
 * Single-sided changes use the side that has content:
 *   addition → 'B' (only the right column has the new content)
 *   deletion → 'A' (only the left column has the doomed content)
 *   move     → both sides exist; left='A', right='B'
 */
export type DecisionRole = 'A' | 'B';

/**
 * Map (decision status, cell role) → final-state display mode.
 *
 *   accept = "采纳右侧/B" → role='B' is winner, role='A' is loser
 *   reject = "保留左侧/A" → role='A' is winner, role='B' is loser
 *
 * Owning views call this both for the inner ChangeMarker (so it repaints)
 * and pass `role` to DecidableChange (so the chip text is correct).
 */
export function displayModeFor(
  status: DecisionStatus,
  role: DecisionRole,
): DecisionDisplay {
  if (status === 'accepted') return role === 'B' ? 'winner' : 'loser';
  if (status === 'rejected') return role === 'A' ? 'winner' : 'loser';
  return 'pending';
}

interface DecidableChangeProps {
  changeId: string;
  /** Used to render context-aware chip copy ("已采纳新版" vs "采纳新位置" etc). */
  changeType: ChangeType;
  status: DecisionStatus;
  meta: DecisionMeta | null;
  pending: boolean;
  /** Roles other than viewer can record decisions; viewer sees status but no overlay. */
  canDecide: boolean;
  onDecide: (action: DecisionAction, note?: string) => void;
  /** Optional navigation adapter — when present, this block participates in
   *  keyboard / minimap navigation (auto-register + selected ring + click-to-select). */
  navigation?: NavigationAdapter;
  /** When ``false``, the hover-revealed accept/reject overlay is suppressed
   *  even though the block still participates in navigation and renders the
   *  status ring/chip. Used by SideBySideView's left mirror cell for
   *  modification/move rows so the action surface lives only on the right
   *  side, matching common review-tool conventions (Word / Gerrit / GitHub).
   *  Defaults to ``true`` (full interactive). */
  interactive?: boolean;
  /** Cell role — see `DecisionRole`. Defaults to `'B'` (right side / the
   *  side that wins on accept). */
  role?: DecisionRole;
  children: ReactNode;
}

const HOVER_REVEAL_MS = 250;

export function DecidableChangeImpl({
  changeId,
  changeType,
  status,
  meta,
  pending,
  canDecide,
  onDecide,
  navigation,
  interactive = true,
  role = 'B',
  children,
}: DecidableChangeProps): React.JSX.Element {
  const [hovered, setHovered] = useState<boolean>(false);
  const hoverTimerRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Register this block with the navigation hook so j/k/minimap can scroll
  // it into view. The effect re-runs whenever the nav adapter swaps (rare —
  // adapter identity is stable per page mount).
  useEffect(() => {
    if (!navigation) return;
    const node = wrapperRef.current;
    if (!node) return;
    navigation.register(changeId, node);
    return () => {
      navigation.register(changeId, null);
    };
  }, [navigation, changeId]);

  const isSelected =
    navigation !== undefined && navigation.selectedChangeId === changeId;

  const handleMouseEnter = useCallback(() => {
    if (!canDecide || !interactive) return;
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = window.setTimeout(() => {
      setHovered(true);
      hoverTimerRef.current = null;
    }, HOVER_REVEAL_MS);
  }, [canDecide, interactive]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHovered(false);
  }, []);

  const display = displayModeFor(status, role);
  const chip = chipFor(changeType, role, status);

  // Outline channel:
  //   - selection (j/k cursor) takes precedence — most transient signal
  //   - winner side gets a 2px solid green ring (the strong "this is the
  //     final content" affordance the M3 B5 review surfaced)
  //   - loser side: no outline. The ChangeMarker is already gray with
  //     strikethrough via decisionDisplay='loser', adding an outline
  //     would compete with the muted skin.
  //   - pending: no outline.
  const wrapperStyle: React.CSSProperties | undefined = (() => {
    if (isSelected) {
      return { outline: `2px solid ${colors.decision['selection-ring']}`, outlineOffset: '2px' };
    }
    if (display === 'winner') {
      return { outline: `2px solid ${colors.decision['winner-border']}`, outlineOffset: '2px' };
    }
    return undefined;
  })();

  const handleClick = useCallback(() => {
    if (!navigation) return;
    navigation.select(changeId);
  }, [navigation, changeId]);

  return (
    <div
      ref={wrapperRef}
      className="group/decidable relative"
      data-change-id={changeId}
      data-decision-status={status}
      data-decision-role={role}
      data-decision-display={display}
      data-selected={isSelected ? 'true' : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={navigation ? handleClick : undefined}
      style={wrapperStyle}
    >
      {children}

      {/* Final-state chip — sits at the top-left so it doesn't collide
          with ChangeMarker's top-right type symbol. Winner = success
          green pill; loser = muted ink pill. The chip carries the
          context-aware copy (e.g. "采纳新位置" vs "已采纳新版"). */}
      {chip && (
        <span
          aria-label={chip.label}
          className={`pointer-events-none absolute -top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold no-underline shadow-sm ${chip.classes}`}
        >
          <span aria-hidden="true">{chip.icon}</span>
          <span>{chip.label}</span>
        </span>
      )}

      {/* Hover overlay — appears 250ms after the mouse settles. Lawyers
          and reviewers see it; viewers do not. ``interactive=false`` (set
          by SideBySideView's left mirror cell) suppresses this overlay so
          the action surface stays single-sided. */}
      {canDecide && interactive && hovered && (
        <DecisionOverlay
          changeId={changeId}
          status={status}
          meta={meta}
          pending={pending}
          onDecide={onDecide}
        />
      )}
    </div>
  );
}

/**
 * Custom comparator for `DecidableChange` (T3 — perf hardening).
 *
 * Why not default shallow compare:
 *
 *   The navigation adapter's `useMemo` deps include `selectedChangeId`,
 *   so whenever the user navigates with `j` / `k` (or clicks the minimap),
 *   the adapter's identity flips. A default-shallow comparator would
 *   re-render EVERY DecidableChange in the tree on every navigation step
 *   even though only the previously-selected and newly-selected rows
 *   actually need to repaint.
 *
 *   Instead we explicitly compare the *truth value* of "is this change
 *   selected" — derived from `navigation?.selectedChangeId === changeId`
 *   on both sides. Other props use shallow equality. `children` is
 *   deliberately NOT compared: callers pass fresh inline JSX on every
 *   render but the rendered content (status chip / outline) is entirely a
 *   function of the compared props, so skipped re-renders produce visually
 *   identical output.
 *
 *   `navigation.register` and `navigation.select` are compared by
 *   reference because they are stable across navigation events (only
 *   `selectedChangeId` flips). If either changes, we re-render so the
 *   mount-time `register` effect re-runs against the new adapter.
 *
 * IMPORTANT — `children` implicit contract (P2-12):
 *
 *   `children` is intentionally NOT compared. The current callers
 *   (`SideBySideView` / `UnifiedView`'s `wrap` closure) always produce a
 *   children tree derived from `changeId` + `status` (both compared above),
 *   so visually skipped re-renders produce identical output. If a future
 *   caller injects external state into children (e.g. annotation counts,
 *   live-collaborator cursors, asynchronous comment badges), this memo
 *   will silently skip re-renders that should repaint the row. Either:
 *     (a) Compare children by reference (default behavior) — requires the
 *         caller to stabilise children with `useCallback` / `useMemo`.
 *     (b) Add an explicit prop for the external state and compare it here.
 *   Until one of those is in place, do NOT pass dynamic content via
 *   children to this component.
 */
export const DecidableChange = memo(DecidableChangeImpl, (prev, next) => {
  const wasSelected = prev.navigation?.selectedChangeId === prev.changeId;
  const isSelected = next.navigation?.selectedChangeId === next.changeId;
  return (
    prev.changeId === next.changeId &&
    prev.changeType === next.changeType &&
    prev.status === next.status &&
    prev.meta === next.meta &&
    prev.pending === next.pending &&
    prev.canDecide === next.canDecide &&
    prev.interactive === next.interactive &&
    prev.role === next.role &&
    wasSelected === isSelected &&
    prev.navigation?.register === next.navigation?.register &&
    prev.navigation?.select === next.navigation?.select
  );
});

interface ChipDescriptor {
  label: string;
  icon: string;
  classes: string;
}

const WINNER_CHIP_CLASSES =
  'border-decision-chip-winner-bg bg-decision-chip-winner-bg text-decision-chip-winner-fg';
const LOSER_CHIP_CLASSES =
  'border-decision-chip-loser-border bg-decision-chip-loser-bg text-decision-chip-loser-fg';

/**
 * Pick the right chip copy based on what the change is and which side this
 * cell represents. We deliberately avoid generic phrases like "已采纳" on
 * single-sided changes so the reviewer can read the chip in isolation and
 * still know what the final document will look like.
 */
function chipFor(
  changeType: ChangeType,
  role: DecisionRole,
  status: DecisionStatus,
): ChipDescriptor | null {
  if (status === 'pending') return null;
  const display = displayModeFor(status, role);
  const isWinner = display === 'winner';
  const cls = isWinner ? WINNER_CHIP_CLASSES : LOSER_CHIP_CLASSES;
  const icon = isWinner ? '✓' : '✕';

  let label: string;
  switch (changeType) {
    case 'modification':
      label =
        role === 'B'
          ? status === 'accepted'
            ? '已采纳新版'
            : '已拒绝改动'
          : status === 'accepted'
            ? '原版已弃用'
            : '原版保留';
      break;
    case 'addition':
      label = status === 'accepted' ? '已采纳新增' : '已拒绝新增';
      break;
    case 'deletion':
      label = status === 'accepted' ? '已确认删除' : '保留原文';
      break;
    case 'move':
      label =
        role === 'B'
          ? status === 'accepted'
            ? '采纳新位置'
            : '拒绝迁移'
          : status === 'accepted'
            ? '旧位置弃用'
            : '保留原位置';
      break;
    case 'format':
      label = status === 'accepted' ? '已采纳格式' : '已拒绝格式';
      break;
    default: {
      const _exhaustive: never = changeType;
      void _exhaustive;
      label = '';
    }
  }

  return { label, icon, classes: cls };
}

interface DecisionOverlayProps {
  changeId: string;
  status: DecisionStatus;
  meta: DecisionMeta | null;
  pending: boolean;
  onDecide: (action: DecisionAction, note?: string) => void;
}

function DecisionOverlay({
  changeId,
  status,
  meta,
  pending,
  onDecide,
}: DecisionOverlayProps): React.JSX.Element {
  const acceptActive = status === 'accepted';
  const rejectActive = status === 'rejected';
  const hasDecision = status !== 'pending';
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  // Pre-fill the textarea with the existing note when the editor opens so
  // the reviewer can tweak rather than re-type.
  useEffect(() => {
    if (noteOpen) setNoteDraft(meta?.note ?? '');
  }, [noteOpen, meta?.note]);

  const submitNote = () => {
    const trimmed = noteDraft.trim();
    // Translate persisted status noun ('accepted'/'rejected') back to the
    // action verb ('accept'/'reject') the decide() API expects. The note-
    // patch path on the server skips toggle-off, so re-asserting the same
    // action with a note updates the rationale in place.
    const action: DecisionAction | null =
      status === 'accepted' ? 'accept' : status === 'rejected' ? 'reject' : null;
    if (action) onDecide(action, trimmed || undefined);
    setNoteOpen(false);
  };

  return (
    <div
      role="group"
      aria-label="审阅操作"
      className="absolute right-0 top-0 z-dropdown -translate-y-2 translate-x-2 rounded-md border border-ink-200 bg-ink-0 p-1 shadow-md"
    >
      <div className="flex items-center gap-1">
        <DecisionButton
          active={acceptActive}
          tone="accept"
          disabled={pending}
          onClick={() => onDecide('accept')}
          label="接受"
          shortcut="a"
          dataTestId={`decision-accept-${changeId}`}
        />
        <DecisionButton
          active={rejectActive}
          tone="reject"
          disabled={pending}
          onClick={() => onDecide('reject')}
          label="拒绝"
          shortcut="r"
          dataTestId={`decision-reject-${changeId}`}
        />
        {hasDecision && (
          <DecisionButton
            active={false}
            tone="pending"
            disabled={pending}
            onClick={() => onDecide('pending')}
            label="待定"
            shortcut="z"
            dataTestId={`decision-pending-${changeId}`}
          />
        )}
      </div>
      {meta && hasDecision && (
        <p className="mt-1 px-1 text-right text-[10px] leading-tight text-ink-500">
          {meta.reviewerDisplayName} · {formatRelativeTime(meta.decidedAt)}
        </p>
      )}
      {hasDecision && !noteOpen && (
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          data-testid={`decision-note-edit-${changeId}`}
          className="mt-1 block w-full rounded-sm px-1 py-0.5 text-right text-[10px] text-ink-500 transition-colors duration-fast hover:bg-ink-100 hover:text-ink-700"
        >
          {meta?.note ? '编辑理由' : '添加理由'}
        </button>
      )}
      {hasDecision && noteOpen && (
        <div
          data-testid={`decision-note-editor-${changeId}`}
          className="mt-1 flex flex-col gap-1 border-t border-ink-200 pt-1"
        >
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="决策理由（可选）"
            maxLength={2000}
            rows={3}
            className="w-56 resize-none rounded-sm border border-ink-200 bg-ink-0 px-1.5 py-1 text-xs leading-snug text-ink-700 focus:border-semantic-info focus:outline-none"
            data-testid={`decision-note-textarea-${changeId}`}
            autoFocus
          />
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setNoteOpen(false)}
              className="rounded-sm px-2 py-0.5 text-[10px] text-ink-500 hover:bg-ink-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={submitNote}
              disabled={pending}
              className="rounded-sm bg-ink-700 px-2 py-0.5 text-[10px] text-ink-0 hover:bg-ink-900 disabled:opacity-60"
              data-testid={`decision-note-save-${changeId}`}
            >
              保存
            </button>
          </div>
        </div>
      )}
      {hasDecision && meta?.note && !noteOpen && (
        <p
          className="mt-1 max-w-56 px-1 text-left text-[10px] leading-snug text-ink-600"
          data-testid={`decision-note-display-${changeId}`}
        >
          {meta.note}
        </p>
      )}
      {hasDecision && <HistoryTimeline changeId={changeId} />}
    </div>
  );
}

const ACTION_LABELS: Record<DecisionHistoryItem['action'], string> = {
  accept: '接受',
  reject: '拒绝',
  pending: '重置',
};

/**
 * P3-2: lazy-loaded flip timeline for one change. Fetches on first expand
 * so the hover overlay stays cheap on the common scroll-through path; the
 * reviewer only pays the round-trip when they actually want the audit trail.
 */
function HistoryTimeline({ changeId }: { changeId: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<DecisionHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || items !== null) return;
    let cancelled = false;
    veridocApi
      .getDecisionHistory(changeId)
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载历史失败');
          setItems([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, changeId, items]);

  return (
    <div className="mt-1 border-t border-ink-200 pt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`decision-history-toggle-${changeId}`}
        className="block w-full rounded-sm px-1 py-0.5 text-right text-[10px] text-ink-500 transition-colors duration-fast hover:bg-ink-100 hover:text-ink-700"
      >
        {open ? '收起历史' : '查看历史'}
      </button>
      {open && (
        <ol
          data-testid={`decision-history-list-${changeId}`}
          className="mt-1 flex max-h-40 flex-col gap-1 overflow-y-auto"
        >
          {items === null && (
            <li className="px-1 text-[10px] text-ink-400">加载中…</li>
          )}
          {error && <li className="px-1 text-[10px] text-semantic-danger">{error}</li>}
          {items !== null && items.length === 0 && !error && (
            <li className="px-1 text-[10px] text-ink-400">暂无历史</li>
          )}
          {items?.map((item) => (
            <li
              key={item.id}
              className="flex flex-col gap-0.5 rounded-sm bg-ink-50 px-1.5 py-1"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] font-medium text-ink-700">
                  {item.reviewerDisplayName}
                  <span className="ml-1 text-ink-400">·</span>
                  <span className="ml-1 text-ink-500">{ACTION_LABELS[item.action]}</span>
                </span>
                <span className="text-[10px] text-ink-400">
                  {formatRelativeTime(item.decidedAt)}
                </span>
              </div>
              {item.note && (
                <p className="text-[10px] leading-snug text-ink-600">{item.note}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

interface DecisionButtonProps {
  active: boolean;
  tone: 'accept' | 'reject' | 'pending';
  disabled: boolean;
  onClick: () => void;
  label: string;
  shortcut: string;
  dataTestId?: string;
}

function DecisionButton({
  active,
  tone,
  disabled,
  onClick,
  label,
  shortcut,
  dataTestId,
}: DecisionButtonProps): React.JSX.Element {
  const palette =
    tone === 'accept'
      ? {
          activeBg: 'bg-semantic-success',
          activeFg: 'text-ink-0',
          idleBg: 'bg-semantic-successBg',
          idleFg: 'text-semantic-success',
          hover: 'hover:bg-semantic-success hover:text-ink-0',
          symbol: '✓',
        }
      : tone === 'reject'
        ? {
            activeBg: 'bg-semantic-danger',
            activeFg: 'text-ink-0',
            idleBg: 'bg-semantic-dangerBg',
            idleFg: 'text-semantic-danger',
            hover: 'hover:bg-semantic-danger hover:text-ink-0',
            symbol: '✕',
          }
        : {
            activeBg: 'bg-semantic-warning',
            activeFg: 'text-ink-0',
            idleBg: 'bg-ink-100',
            idleFg: 'text-ink-600',
            hover: 'hover:bg-ink-200 hover:text-ink-900',
            symbol: '↺',
          };

  const tones = active
    ? `${palette.activeBg} ${palette.activeFg}`
    : `${palette.idleBg} ${palette.idleFg} ${palette.hover}`;
  const disabledTones = disabled ? 'cursor-wait opacity-60' : '';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={`${label} (${shortcut})`}
      data-testid={dataTestId}
      className={`inline-flex h-6 items-center gap-1 rounded-sm px-2 text-xs font-semibold transition-colors duration-fast ${tones} ${disabledTones}`}
    >
      <span aria-hidden="true">{palette.symbol}</span>
      <span>{label}</span>
      <kbd
        aria-hidden="true"
        className="ml-1 rounded-sm border border-current/30 px-1 text-[10px] opacity-70"
      >
        {shortcut}
      </kbd>
    </button>
  );
}

/**
 * Format an ISO timestamp as a relative phrase ("5 分钟前", "刚刚",
 * "2 天前"). Uses `Intl.RelativeTimeFormat` for locale-aware output and
 * falls back to a manual approximation when the runtime lacks support.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const diffMs = then.getTime() - now.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const absSeconds = Math.abs(diffSeconds);

  if (absSeconds < 30) return '刚刚';

  const formatter = new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' });
  const ranges: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];
  for (const [unit, secondsInUnit] of ranges) {
    if (absSeconds >= secondsInUnit || unit === 'second') {
      const value = Math.round(diffSeconds / secondsInUnit);
      return formatter.format(value, unit);
    }
  }
  return '';
}

export default DecidableChange;
